import type { ChangeEventHandler, FunctionComponent } from 'react';
import { useCallback, useState } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { sendErrorEvent } from '@trello/error-reporting';
import { forTemplate } from '@trello/legacy-i18n';
import { Button } from '@trello/nachos/button';
import { Dialog, useDialog } from '@trello/nachos/dialog';
import { KeyIcon } from '@trello/nachos/icons/key';
import { Textfield } from '@trello/nachos/textfield';
import type { AppManagementTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';
import { token } from '@trello/theme';

import { useCreatePluginApplicationMutation } from '../queries/CreatePluginApplicationMutation.generated';
import { usePluginApplicationQuery } from '../queries/PluginApplicationQuery.generated';
import { GenerateApiKeyModal } from './GenerateApiKeyModal';

import styles from './PowerUpGenerateApiKeyPage.less';

const format = forTemplate('app_management');

export const PowerUpGenerateApiKeyPage: FunctionComponent<{
  idPlugin: string;
  onGenerate: () => void;
}> = ({ onGenerate, idPlugin }) => {
  const [apiKey, setApiKey] = useState<string>('');
  const [isInvalidApiKey, setIsInvalidApiKey] = useState(false);
  const [unauthorizedApiKey, setUnauthorizedApiKey] = useState(false);
  const [createPluginApplicationMutation] =
    useCreatePluginApplicationMutation();
  const { data: pluginApplicationData } = usePluginApplicationQuery({
    variables: {
      pluginId: idPlugin,
      getMemberApplication: true,
    },
    waitOn: ['None'],
  });

  const generateApiKey = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (existingApiKey: any = null) =>
      async () => {
        const taskName = 'edit-plugin/generateApiKey';
        const source = 'powerUpEditScreen';
        if (/^[a-f0-9]{32}$/.test(existingApiKey) || existingApiKey === null) {
          const traceId = Analytics.startTask({
            taskName,
            source,
          });
          try {
            await createPluginApplicationMutation({
              variables: {
                pluginId: idPlugin,
                key: existingApiKey,
              },
            });
            onGenerate();
            Analytics.taskSucceeded({
              taskName,
              traceId,
              source,
            });
          } catch (error) {
            setUnauthorizedApiKey(true);
            setIsInvalidApiKey(false);
            Analytics.taskFailed({
              taskName,
              traceId,
              source,
              error,
            });
            sendErrorEvent(error, {
              tags: { ownershipArea: 'trello-web-eng' },
            });
          }
        }
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const value = e.target.value;
      setApiKey(value);
      if (/^[a-f0-9]{32}$/.test(value)) {
        setIsInvalidApiKey(false);
      } else {
        setIsInvalidApiKey(true);
        setUnauthorizedApiKey(false);
      }
    },
    [setApiKey, setIsInvalidApiKey],
  );

  const isOldApiKeyConvertedToPlugin = !pluginApplicationData;

  const { dialogProps, show, hide, toggle } = useDialog();

  return (
    <div
      data-testid={getTestId<AppManagementTestIds>(
        'power-up-generate-api-key-page',
      )}
    >
      <div className={styles.header}>
        <h1>{format('api-key')}</h1>
      </div>

      <div className={styles.generate}>
        <div className={styles.generateInfo}>
          <div className={styles.iconKey}>
            <KeyIcon
              size="large"
              color={token('color.icon.accent.purple', '#8270DB')}
            />
          </div>
          <div>
            <h3>{format('better-way-to-manage-api-keys')}</h3>
            <p
              // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml -- FIXME
              dangerouslySetInnerHTML={{
                __html:
                  format('api-key-introduction') +
                  ' ' +
                  (isOldApiKeyConvertedToPlugin
                    ? format('to-get-started-generate-a-new-api-key')
                    : format('use-existing-or-generate-a-new-api-key')),
              }}
            />
          </div>
        </div>
        <div className={styles.generate}>
          {isOldApiKeyConvertedToPlugin ? null : (
            <div>
              <Textfield
                label={format('api-key')}
                placeholder={format('enter-existing-api-key')}
                className={styles.textfield}
                iconAfter={
                  <Button
                    className={styles.submitBtn}
                    size="wide"
                    children={format('use-existing-api-key')}
                    appearance="primary"
                    onClick={generateApiKey(apiKey)}
                  />
                }
                onChange={onChange}
                isInvalid={isInvalidApiKey}
              />
              {isInvalidApiKey && (
                <span
                  className={styles.isInvalidApiKey}
                  data-testid={getTestId<AppManagementTestIds>(
                    'power-up-existing-api-key-invalid',
                  )}
                >
                  {format('not-a-valid-api-key')}
                </span>
              )}
              {unauthorizedApiKey && (
                <span
                  className={styles.isInvalidApiKey}
                  data-testid={getTestId<AppManagementTestIds>(
                    'power-up-existing-api-key-invalid',
                  )}
                >
                  {format('unauthorized-api-key')}
                </span>
              )}
              <div className={styles.optionSeparator}>{format('or')}</div>
            </div>
          )}
          <Button
            children={format('generate-a-new-api-key')}
            className={styles.generateBtn}
            appearance={'default'}
            onClick={show}
          />
        </div>
      </div>

      <Dialog
        {...dialogProps}
        show={show}
        hide={hide}
        toggle={toggle}
        size="large"
        alignment={'center'}
        className={styles.dialog}
      >
        <GenerateApiKeyModal generateApiKey={generateApiKey} hide={hide} />
      </Dialog>
    </div>
  );
};
